import { useRouteError, isRouteErrorResponse, useNavigate } from 'react-router-dom'
import { useEffect } from 'react'
import { AppTemplate } from '@components/templates/AppTemplate/AppTemplate'
import { Typography } from '@components/atoms/Typography/Typography'
import { Button } from '@components/ui/Button/Button'
import { Card, CardContent } from '@components/ui/Card/Card'
import { ROUTES } from '@constants/routes'
import { Sentry } from '@lib/sentry/sentry'

export function ErrorBoundary() {
  const error = useRouteError()
  const navigate = useNavigate()

  let errorMessage: string
  let errorStatus: number | undefined
  let errorTitle: string
  let errorHint: string | undefined

  // Capture error to Sentry
  useEffect(() => {
    if (error) {
      // Set context for better debugging
      Sentry.setContext('errorBoundary', {
        errorType: isRouteErrorResponse(error) ? 'RouteError' : error instanceof Error ? 'Error' : 'Unknown',
        errorStatus: isRouteErrorResponse(error) ? error.status : undefined,
        timestamp: new Date().toISOString()
      })

      // Capture the error
      if (isRouteErrorResponse(error)) {
        Sentry.captureException(new Error(`Route Error ${error.status}: ${error.statusText}`), {
          level: error.status >= 500 ? 'error' : 'warning',
          tags: {
            errorType: 'route',
            statusCode: error.status.toString()
          }
        })
      } else if (error instanceof Error) {
        Sentry.captureException(error, {
          level: 'error',
          tags: {
            errorType: 'runtime',
            component: 'ErrorBoundary'
          }
        })
      } else {
        Sentry.captureMessage('Unknown error in ErrorBoundary', {
          level: 'error',
          extra: { error }
        })
      }
    }
  }, [error])

  if (isRouteErrorResponse(error)) {
    errorStatus = error.status

    // Messages contextuels selon le code d'erreur
    switch (error.status) {
      case 400:
        errorTitle = 'Requête invalide'
        errorMessage = typeof error.data === 'string' ? error.data : 'Les paramètres fournis sont invalides.'
        errorHint = "Veuillez vérifier l'URL et réessayer."
        break

      case 404:
        errorTitle = 'Ressource introuvable'
        errorMessage =
          typeof error.data === 'string' ? error.data : "La page ou le rendez-vous que vous recherchez n'existe pas."
        errorHint = "Vérifiez l'identifiant fourni ou retournez à l'accueil."
        break

      case 500:
        errorTitle = 'Erreur serveur'
        errorMessage = typeof error.data === 'string' ? error.data : 'Une erreur est survenue sur le serveur.'
        errorHint = 'Veuillez réessayer dans quelques instants.'
        break

      default:
        errorTitle = 'Une erreur est survenue'
        errorMessage =
          error.statusText || (typeof error.data === 'string' ? error.data : 'Une erreur inattendue est survenue.')
        break
    }
  } else if (error instanceof Error) {
    errorTitle = 'Erreur inattendue'
    errorMessage = error.message
    errorHint = "Si le problème persiste, veuillez contacter l'administrateur."
  } else {
    errorTitle = 'Erreur inattendue'
    errorMessage = 'Une erreur inattendue est survenue'
  }

  const handleGoHome = () => {
    navigate(ROUTES.home)
  }

  const handleGoBack = () => {
    navigate(-1)
  }

  const handleReportFeedback = () => {
    // Open Sentry user feedback dialog
    Sentry.showReportDialog({
      eventId: Sentry.lastEventId(),
      title: 'Signaler un problème',
      subtitle: 'Notre équipe a été notifiée. Vous pouvez nous aider en partageant plus de détails.',
      subtitle2: '',
      labelName: 'Nom',
      labelEmail: 'Email',
      labelComments: "Que s'est-il passé ?",
      labelClose: 'Fermer',
      labelSubmit: 'Envoyer',
      errorGeneric: "Une erreur s'est produite lors de l'envoi. Veuillez réessayer.",
      errorFormEntry: 'Certains champs sont invalides. Veuillez les corriger et réessayer.',
      successMessage: 'Merci pour votre retour !'
    })
  }

  return (
    <AppTemplate className="min-h-screen">
      <div className="flex flex-1 items-center justify-center p-8">
        <Card className="max-w-2xl w-full">
          <CardContent className="flex flex-col items-center gap-6 p-8 text-center">
            {errorStatus && <div className="text-6xl font-bold text-muted-foreground opacity-50">{errorStatus}</div>}

            <div className="space-y-3">
              <h1 className="text-3xl font-bold text-foreground">{errorTitle}</h1>
              <Typography type="body" size="lg" className="text-foreground">
                {errorMessage}
              </Typography>
              {errorHint && (
                <Typography type="body" size="md" className="text-muted-foreground">
                  {errorHint}
                </Typography>
              )}
            </div>

            <div className="flex gap-4 mt-4 flex-wrap justify-center">
              <Button variant="secondary" onClick={handleGoBack}>
                Retour
              </Button>
              <Button variant="default" onClick={handleGoHome}>
                Retour à l&apos;accueil
              </Button>
              {/* Only show feedback button in production or if error is severe */}
              {(import.meta.env.PROD || (errorStatus && errorStatus >= 500)) && (
                <Button variant="outline" onClick={handleReportFeedback}>
                  Signaler le problème
                </Button>
              )}
            </div>

            {process.env.NODE_ENV === 'development' && error instanceof Error && error.stack && (
              <details className="mt-4 w-full rounded-lg bg-card border border-border p-4 text-left">
                <summary className="cursor-pointer text-sm font-semibold text-card-foreground">
                  Détails de l&apos;erreur (dev)
                </summary>
                <pre className="mt-2 overflow-auto text-xs text-muted-foreground">{error.stack}</pre>
              </details>
            )}
          </CardContent>
        </Card>
      </div>
    </AppTemplate>
  )
}
